import { LocalStorageKeys } from '@/constants/LocalStorageKeys';
import SymbolDetailsUtil from '@/models/common/SymbolDetailsUtil.';
import IComp from '@/models/Comp';
import ICompAddress from '@/models/CompAddress';
import DoubleIndex from '@/models/DoubleIndex';
import IFieldRuleData from '@/models/FieldRuleData';
import { ISourceColumnOrder } from '@/models/IUserSettings';
import { Getters, store } from '@/store';
import theLocalStorageUtils from '@/utils/LocalStorageUtils';
import LogManager from './LogManager';

/**
 * A Log manager class providing specific methods. This gets them out of the Vue
 * components, reducing size, providing for reuse and allowing debugging.
 */
export default {
    /**
     * Message is 'Data Discrepancy loaded'
     */
    async loadedDiscrepancy() {
        const compCount = store.getters[Getters.CompAddresses.CompCount] as number;
        const formCode = theLocalStorageUtils.get(LocalStorageKeys.formCode);
        const compId = theLocalStorageUtils.get(LocalStorageKeys.almCompId);

        const logMsg = LogManager.createLogMsg(14003, 'Data Discrepancy loaded', compCount);
        LogManager.setStrings(logMsg, formCode ?? '', compId ?? '');
        LogManager.sendUsage(logMsg);

        const logMsg2 = LogManager.createLogMsg(14042, 'QuickSource Vue Launched');
        LogManager.setStrings(logMsg2, 'yes');
        LogManager.sendUsage(logMsg2);
    },

    /**
     * Message is 'QuickSource data exported to the report'.
     */
    async exportedQuickSourceData(compCount: number) {
        const compId = theLocalStorageUtils.get(LocalStorageKeys.almCompId);

        const logMsg = LogManager.createLogMsg(14004, 'QuickSource data exported to the report', compCount);
        LogManager.setNumerics(logMsg, 1);
        LogManager.setStrings(logMsg, '', compId ?? '');
        LogManager.sendUsage(logMsg);
    },

    /**
     * Message is 'QuickSource closed without exporting data to report'
     */
    async closedWithoutSave() {
        const logMsg = LogManager.createLogMsg(14005, 'QuickSource closed without exporting data to report');
        LogManager.sendUsage(logMsg);
    },

    /**
     * Message is 'User attempted to manually refresh Data Source'
     */
    async manualRefreshDataSource(initials: string) {
        const symbolDetails = SymbolDetailsUtil.getBySymbol(initials);
        const sourceSystemId = symbolDetails.id ? Number(symbolDetails.id) : 0;

        const logMsg = LogManager.createLogMsg(14010, 'User attempted to manually refresh Data Source');
        LogManager.setNumerics(logMsg, sourceSystemId);
        LogManager.sendUsage(logMsg);
    },

    /**
     * Message is "User selected 'Use All Data' for Data Source"
     */
    async useAllData(initials: string) {
        const activeComp = store.getters[Getters.CompAddresses.ActiveComp] as IComp;
        const symbolDetails = SymbolDetailsUtil.getBySymbol(initials);
        const sourceSystemId = symbolDetails.id ? Number(symbolDetails.id) : 0;

        const logMsg = LogManager.createLogMsg(14011, "User selected 'Use All Data' for Data Source", activeComp.compNumber);
        LogManager.setNumerics(logMsg, sourceSystemId);
        LogManager.sendUsage(logMsg);
    },

    /**
     * Message is 'User enabled "Only Show Data Discrepancies"'
     */
    async enabledShowDataDiscrepancies() {
        const logMsg = LogManager.createLogMsg(14012, 'User enabled "Only Show Data Discrepancies"');
        LogManager.sendUsage(logMsg);
    },

    /**
     * Message is 'User disabled "Only Show Data Discrepancies"'
     */
    async disabledShowDataDiscrepancies() {
        const logMsg = LogManager.createLogMsg(14013, 'User disabled "Only Show Data Discrepancies"');
        LogManager.sendUsage(logMsg);
    },

    /**
     * Message is 'QuickSource Subject Tab Load' or 'QuickSource Comp Tab Load'
     * @param comp
     */
    async loadedCompTab(comp: IComp) {
        let recordVersionsDB = 0;
        if (comp.sourceDataDB?.records) {
            recordVersionsDB = comp.sourceDataDB.records;
        }

        let recordVersionsSE = 0;
        if (comp.sourceDataSE?.records) {
            recordVersionsSE = comp.sourceDataSE.records;
        }

        if (comp.isSubject) {
            const logMsg = LogManager.createLogMsg(14034, 'QuickSource Subject Tab Load', 0);
            LogManager.setNumerics(logMsg, recordVersionsDB, recordVersionsSE);
            LogManager.sendUsage(logMsg);
        } else {
            const logMsg = LogManager.createLogMsg(14015, 'QuickSource Comp Tab Load', comp.compNumber);
            LogManager.setNumerics(logMsg, recordVersionsDB, recordVersionsSE);
            LogManager.sendUsage(logMsg);
        }
    },

    /**
     * Message is 'SmartExchange Data Exported to Report'.
     * @param dataExists
     */
    async exportedSmartExchangeData(dataExists: boolean) {
        const logMsg = LogManager.createLogMsg(14016, 'SmartExchange Data Exported to Report', dataExists ? 1 : 0);
        LogManager.sendUsage(logMsg);
    },

    /**
     * Message is 'User clicked Remind Me Later in First Time User Tutorial'
     * @param step
     */
    async remindMeLaterFirstTimeTutorial(step: number) {
        const logMsg = LogManager.createLogMsg(14017, 'User clicked Remind Me Later in First Time User Tutorial');
        LogManager.setStrings(logMsg, '' + step);
        LogManager.sendUsage(logMsg);
    },

    /**
     * Message is 'User completed First Time User Tutorial'
     * @param importCount
     */
    async completedFirstTimeTutorial() {
        const logMsg = LogManager.createLogMsg(14018, 'User completed First Time User Tutorial');
        LogManager.sendUsage(logMsg);
    },

    /**
     * Message is 'MLS Data Import'
     * @param importCount
     */
    async importedMlsData(importCount: number) {
        const logMsg = LogManager.createLogMsg(14019, 'MLS Data Import', importCount);
        LogManager.sendUsage(logMsg);
    },

    /**
     * Message is 'Public Records Data Exported to Report'.
     * @param dataExists
     */
    async exportedPublicRecordsData(dataExists: boolean) {
        const logMsg = LogManager.createLogMsg(14020, 'Public Records Data Exported to Report', dataExists ? 1 : 0);
        LogManager.sendUsage(logMsg);
    },

    /**
     * Message is 'User revisited the First-Time User Tutorial'
     */
    async revisitedFirstTimeTutorial() {
        const logMsg = LogManager.createLogMsg(14021, 'User revisited the First-Time User Tutorial');
        LogManager.sendUsage(logMsg);
    },

    /**
     * Message is 'User clicked Import MLS Data button'
     */
    async importingMlsData() {
        const logMsg = LogManager.createLogMsg(14022, 'User clicked Import MLS Data button');
        LogManager.sendUsage(logMsg);
    },

    /**
     * Message is 'User set custom column order'
     * @param sourceColumnOrder
     */
    async setCustomColumnOrder(sourceColumnOrder: ISourceColumnOrder[]) {
        // Data numerics get ordinals 0 & 2, Data strings not including string 1 get ordinals 1 & 3
        const logMsg = LogManager.createLogMsg(14023, 'User set custom column order', sourceColumnOrder[0].id);
        LogManager.setNumerics(logMsg, sourceColumnOrder[2].id);
        LogManager.setStrings(logMsg, '' + sourceColumnOrder[1].id, '' + sourceColumnOrder[3].id);
        LogManager.sendUsage(logMsg);
    },

    /**
     * Message is 'User Altered Data Source Visibility'.
     */
    async alteredDataSourceVisibility(first: boolean, second: boolean, third: boolean, fourth: boolean, showPreferences: boolean) {
        const logMsg = LogManager.createLogMsg(14024, 'User Altered Data Source Visibility', first ? 1 : 0);
        LogManager.setNumerics(logMsg, third ? 1 : 0, showPreferences ? 1 : 0);
        LogManager.setStrings(logMsg, second ? '1' : '0', fourth ? '1' : '0');
        LogManager.sendUsage(logMsg);
    },

    /**
     * Message is 'Data Result Case Override'
     */
    async overrideCase(preferredCasingId: number) {
        const logMsg = LogManager.createLogMsg(14025, 'Data Result Case Override', preferredCasingId);
        LogManager.sendUsage(logMsg);
    },

    /**
     * Message is 'User Presented with Jumpstart your experience Modal'
     */
    async jumpStartYourExperience() {
        const logMsg = LogManager.createLogMsg(14026, 'User Presented with Jumpstart your experience Modal');
        LogManager.sendUsage(logMsg);
    },

    /**
     * Message is 'User clicked Add Comp Manually button'
     */
    async addCompManually() {
        const logMsg = LogManager.createLogMsg(14027, 'User clicked Add Comp Manually button');
        LogManager.sendUsage(logMsg);
    },

    /**
     * Message is 'User set Custom Column and Cascade Order from JumpStart Dialogue'
     */
    async setCustomColumnAndCascadeOrder() {
        const logMsg = LogManager.createLogMsg(14028, 'User set Custom Column and Cascade Order from JumpStart Dialogue');
        LogManager.sendUsage(logMsg);
    },

    /**
     * Message is 'User Clicked Remind Me Later on JumpStart Dialogue'
     */
    async remindMeLaterOnJumpStart() {
        const logMsg = LogManager.createLogMsg(14029, 'User Clicked Remind Me Later on JumpStart Dialogue');
        LogManager.sendUsage(logMsg);
    },

    /**
     * Message is 'User Clicked Skip on JumpStart Dialogue'
     */
    async skipOnJumpStart() {
        const logMsg = LogManager.createLogMsg(14030, 'User Clicked Skip on JumpStart Dialogue');
        LogManager.sendUsage(logMsg);
    },

    /**
     * Message is 'User Created Translation Rule'
     */
    async createdTranslationRule(fieldRule: IFieldRuleData, fieldId: number) {
        const logMsg = LogManager.createLogMsg(14031, 'User Created Translation Rule', fieldRule.textMatchOption);
        LogManager.setNumerics(logMsg, fieldRule.textEditOption, fieldId);
        LogManager.setStrings(logMsg, fieldRule.textMatchCriteria, fieldRule.textEditValue);
        LogManager.sendUsage(logMsg);
    },
    /**
     * Message is 'User Edited Translation Rule'
     */
    async editedTranslationRule(fieldRule: IFieldRuleData, fieldId: number) {
        const logMsg = LogManager.createLogMsg(14032, 'User Edited Translation Rule', fieldRule.textMatchOption);
        LogManager.setNumerics(logMsg, fieldRule.textEditOption, fieldId);
        LogManager.setStrings(logMsg, fieldRule.textMatchCriteria, fieldRule.textEditValue);
        LogManager.sendUsage(logMsg);
    },
    /**
     * Message is 'User Deleted Translation Rule'
     */
    async deletedTranslationRule(fieldRule: IFieldRuleData, fieldId: number) {
        const logMsg = LogManager.createLogMsg(14033, 'User Deleted Translation Rule', fieldRule.textMatchOption);
        LogManager.setNumerics(logMsg, fieldRule.textEditOption, fieldId);
        LogManager.setStrings(logMsg, fieldRule.textMatchCriteria, fieldRule.textEditValue);
        LogManager.sendUsage(logMsg);
    },

    /**
     * Message is 'User clicked "Hide" sub comps'
     */
    async hideSubComps(initials: string) {
        const logMsg = LogManager.createLogMsg(14035, 'User clicked "Hide" sub comps');
        LogManager.setStrings(logMsg, initials);
        LogManager.sendUsage(logMsg);
    },

    /**
     * Message is 'User clicked "Show" sub comps'
     */
    async showSubComps(initials: string) {
        const logMsg = LogManager.createLogMsg(14036, 'User clicked "Show" sub comps');
        LogManager.setStrings(logMsg, initials);
        LogManager.sendUsage(logMsg);
    },

    /**
     * Message is 'User Rearranged Comp Tabs'
     */
    async rearrangedCompTabs(address: ICompAddress, indexes: DoubleIndex, dragged: boolean) {
        const addressLine = `${address.address1} ${address.unitNumber} ${address.city} ${address.state} ${address.zipCode}`;

        const logMsg = LogManager.createLogMsg(14037, 'User Rearranged Comp Tabs', indexes.source);
        LogManager.setNumerics(logMsg, indexes.target);
        LogManager.setStrings(logMsg, addressLine, dragged ? 'drag' : 'click');
        LogManager.sendUsage(logMsg);
    },

    /**
     * Message is 'User Deleted Comp Tabs'
     */
    async deletedCompTabs(address: ICompAddress, index: number) {
        const addressLine = `${address.address1} ${address.unitNumber} ${address.city} ${address.state} ${address.zipCode}`;

        const logMsg = LogManager.createLogMsg(14038, 'User Deleted Comp Tabs', index);
        LogManager.setStrings(logMsg, addressLine);
        LogManager.sendUsage(logMsg);
    },

    /**
     * Message is 'MLS Data Import Error'
     */
    async importMlsDataError(address: ICompAddress) {
        const logMsg = LogManager.createLogMsg(14039, 'MLS Data Import Error');
        LogManager.setStrings(logMsg, JSON.stringify(address));
        LogManager.sendUsage(logMsg);
    },

    /**
     * Message is 'Send all properties to TOTAL'.
     */
    async sendAllPropertiesToTotal(exportAllComps: boolean) {
        const logMsg = LogManager.createLogMsg(14040, 'Send all properties to TOTAL', exportAllComps ? 1 : 0);
        LogManager.sendUsage(logMsg);
    },

    /**
     * Message is 'MLS Data Exported To Report'.
     * @param dataExists
     */
    async exportedMlsData(dataExists: boolean) {
        const logMsg = LogManager.createLogMsg(14041, 'MLS Data Exported To Report', dataExists ? 1 : 0);
        LogManager.sendUsage(logMsg);
    },

    /**
     * Message is 'User Launched View Photos'.
     */
    async launchedViewPhotos() {
        const logMsg = LogManager.createLogMsg(14043, 'User Launched View Photos');
        LogManager.sendUsage(logMsg);
    },

    /**
     * Message is 'User saved Photo View changes to the grid'.
     */
    async savedPhotoViewChanges() {
        const logMsg = LogManager.createLogMsg(14044, 'User saved Photo View changes to the grid');
        LogManager.sendUsage(logMsg);
    },

    /**
     * Message is 'User Launched Sale History'.
     */
    async launchedSalesHistory() {
        const logMsg = LogManager.createLogMsg(14045, 'User Launched Sale History');
        LogManager.sendUsage(logMsg);
    },

    /**
     * Message is 'User saved Sale History data to the grid'.
     */
    async savedSalesHistory() {
        const logMsg = LogManager.createLogMsg(14046, 'User saved Sale History data to the grid');
        LogManager.sendUsage(logMsg);
    },

    /**
     * Message is 'Local Comp DB Data Exported to Report'.
     * @param dataExists
     */
    async exportedLocalCompDBData(dataExists: boolean) {
        const logMsg = LogManager.createLogMsg(14047, 'Local Comp DB Data Exported to Report', dataExists ? 1 : 0);
        LogManager.sendUsage(logMsg);
    },
};
